<template>
  <div ref="containerRef" class="flex flex-col" :class="[!hideLabels && 'pt-6']">
    <div
      class="relative mx-auto flex h-3 flex-row"
      :style="{ width: `calc(${(100 / scale.length) * (scale.length - 1)}%)` }"
    >
      <PerspectiveValueBarLabels
        v-if="!hideLabels && labelOffset > 0"
        class="absolute -top-6.5 z-0 w-full"
        :edge-overflow="labelOffset"
        :labels="
          dataPoints.map((dp) => ({
            left: dp.left,
            texts: dp.perspectives.map((p) =>
              resolvePerspectiveLabel(p.perspective.key, [p.perspective], {
                kind: 'nameShort',
                count: p.numberOfFeedbacks,
                selfLabel: t('perspectives.self-short', { count: dp.numberOfFeedbacks }),
              }),
            ),
          }))
        "
      />
      <template v-for="(point, index) in dataPoints" :key="point.value">
        <div
          v-if="index !== 0"
          :style="point.bar"
          class="bg-tertiary-container rounded-extra-small absolute z-20 h-full px-0.5"
        >
          <BaseWavyProgressIndicator
            class="stroke-tertiary h-full"
            :thickness="6"
            :wavelength="16"
          />
        </div>
        <div
          v-else
          class="rounded-extra-small bg-primary absolute h-full"
          :class="[index === 0 && 'rounded-l-large']"
          :style="point.bar"
        ></div>
      </template>

      <div
        v-for="point in dataPoints"
        :key="point.value"
        class="absolute top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center"
        :style="{
          left: point.left,
          width: point.dotSize + 'px',
          height: DOT_SIZE_MATCH + 'px',
        }"
      >
        <PerspectiveDotNew
          class="ease-fast-spatial transition-transform duration-200"
          :class="[
            animation >= point.value - 0.05 && 'scale-100',
            animation < point.value - 0.05 && !disableAnimation && 'scale-0',
          ]"
          :perspectives="point.perspectives.map((p) => p.perspective)"
          :all-perspectives="allPerspectives"
          :disable-animation="disableAnimation"
        />
      </div>
      <div
        class="rounded-r-large bg-surface-container-high rounded-l-small absolute h-full"
        :class="[lastPoint === null && 'rounded-l-large!']"
        :style="{
          left:
            lastPoint === null
              ? `-${DOT_SIZE / 2}px`
              : `calc(${lastPoint.left} + ${lastPoint.dotSize / 2 + GAP_SIZE}px)`,
          right: `-${DOT_SIZE / 2}px`,
        }"
      ></div>

      <div
        v-for="(markerPoint, index) in markerPoints"
        :key="index"
        class="bg-on-primary absolute top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 rounded-full"
        :style="{ left: markerPoint.left, width: MARKER_SIZE + 'px', height: MARKER_SIZE + 'px' }"
        :class="markerPoint.color"
      ></div>
    </div>
    <div v-if="!hideLabels" class="flex w-full flex-row pt-2.5">
      <div
        v-for="scaleItem in scale"
        :key="scaleItem.value"
        class="flex shrink-0 grow basis-0 flex-col items-center"
      >
        <div class="text-body-small text-outline text-center">
          <span>{{ scaleItem.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
  import BaseWavyProgressIndicator from '../../../../core/components/base/progress-indicator/BaseWavyProgressIndicator.vue'
  import { DO_PERSPECTIVES_MATCH_THRESHOLD } from '../../../models/question-calculation-config.ts'
  import { useElementBounding, useTransition } from '@vueuse/core'
  import { type Perspective } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useI18n } from 'vue-i18n'
  import PerspectiveValueBarLabels from './PerspectiveValueBarLabels.vue'
  import PerspectiveDotNew from './PerspectiveDotNew.vue'

  interface Props {
    values: ReadonlyArray<{
      perspective: Perspective
      average: number | null
      numberOfFeedbacks: number
    }>
    allPerspectives: ReadonlyArray<Perspective>
    disableAnimation?: boolean
    scale: ReadonlyArray<{ value: number; label: string }>
    isCenterBased?: boolean
    hideLabels?: boolean
    hideNumberOfFeedbacks?: boolean
    animationDelay?: number
  }

  const props = defineProps<Props>()

  const DOT_SIZE = 16
  const MARKER_SIZE = 4
  const DOT_SIZE_MATCH = 32
  const DOT_SIZE_SEMI_MATCH = 22
  const GAP_SIZE = 2

  const containerRef = useTemplateRef<HTMLDivElement>('containerRef')
  const { width } = useElementBounding(containerRef)

  const labelOffset = computed(() => width.value / props.scale.length / 2)

  const { t } = useI18n()

  const dataPoints = computed(() => {
    const result: {
      value: number
      perspectives: Perspective[]
      numberOfFeedbacks: number
    }[] = []

    for (const entry of props.values) {
      if (typeof entry.average === 'number') {
        result.push({
          value: entry.average,
          perspectives: [entry.perspective],
          numberOfFeedbacks: entry.numberOfFeedbacks,
        })
      }
    }

    const sorted = result.sort((a, b) => a.value - b.value)
    const merged: {
      value: number
      perspectives: { perspective: Perspective; numberOfFeedbacks: number }[]
      numberOfFeedbacks: number
    }[] = []

    for (const point of sorted) {
      if (
        merged.length &&
        Math.abs(merged[merged.length - 1]!.value - point.value) <= DO_PERSPECTIVES_MATCH_THRESHOLD
      ) {
        const last = merged[merged.length - 1]!
        last.perspectives.push(
          ...point.perspectives.map((p) => ({
            perspective: p,
            numberOfFeedbacks: last.numberOfFeedbacks,
          })),
        )
        last.value = (last.value + point.value) / 2
        last.numberOfFeedbacks = last.numberOfFeedbacks + point.numberOfFeedbacks
      } else {
        merged.push({
          ...point,
          perspectives: point.perspectives.map((p) => ({
            perspective: p,
            numberOfFeedbacks: point.numberOfFeedbacks,
          })),
        })
      }
    }

    return merged.map((point, idx) => {
      const prevDot = idx > 0 ? merged[idx - 1]! : null
      const prevValue = prevDot ? prevDot.value : 0
      const dotSize = getDotSize(point.perspectives.map((p) => p.perspective))
      const prevDotSize = prevDot
        ? getDotSize(prevDot.perspectives.map((p) => p.perspective))
        : DOT_SIZE
      return {
        perspectives: point.perspectives,
        dotSize: dotSize,
        value: point.value,
        left: positionLeft(point.value),
        bar: getWidthBetween(
          prevValue,
          props.disableAnimation ? point.value : Math.min(point.value, animation.value),
          dotSize,
          prevDotSize,
          idx == 0,
        ),
        numberOfFeedbacks: point.numberOfFeedbacks,
      }
    })
  })

  const lastPoint = computed(() => {
    return dataPoints.value.length > 0 ? dataPoints.value[dataPoints.value.length - 1]! : null
  })

  const markerPoints = computed(() => {
    const part = 100 / (props.scale.length - 1)
    return props.scale.map((scaleItem, index) => {
      return {
        color:
          lastPoint.value === null || scaleItem.value > lastPoint.value.value
            ? 'bg-outline'
            : 'bg-on-primary',
        left: `${index * part}%`,
      }
    })
  })

  const getDotSize = (perspectives: Perspective[]) => {
    if (perspectives.length === props.allPerspectives.length) {
      return DOT_SIZE_MATCH
    }
    if (perspectives.length > 1) {
      return DOT_SIZE_SEMI_MATCH
    }
    return DOT_SIZE
  }

  const maxValue = computed(() => {
    return Math.max(0, ...props.values.map((p) => p.average ?? 0))
  })

  const positionLeft = (value: number) => {
    const percentagePosition = value * 100

    return `${percentagePosition}%`
  }

  const getWidthBetween = (
    start: number,
    end: number,
    dotSize: number,
    prevDotSize: number,
    isFirst: boolean,
  ) => {
    const leftPercent = start * 100
    const rightPercent = 100 - end * 100

    let offsetLeft = 0
    if (isFirst) {
      offsetLeft = DOT_SIZE / -2
    } else {
      offsetLeft = prevDotSize / 2 + GAP_SIZE
    }
    const offsetRight = dotSize / 2 + GAP_SIZE

    return {
      left: `calc(${leftPercent}% + ${offsetLeft}px)`,
      right: `calc(${rightPercent}% + ${offsetRight}px)`,
    }
  }

  const ANIMATION_DURATION = 400
  const animationValue = ref(0)

  const animation = useTransition(animationValue, {
    duration: ANIMATION_DURATION,
    transition: [0.34, 0.8, 0.34, 1],
  })

  const delay = computed(() => {
    return Math.min(1500, props.animationDelay ?? 0)
  })

  watch(
    () => props.allPerspectives,
    async () => {
      animationValue.value = 0
      nextTick(() => {
        setTimeout(() => {
          animationValue.value = maxValue.value
        }, delay.value)
      })
    },
    { immediate: true },
  )
</script>
